<template>
  <div class="fts-card-view">
    <div class="card-view-item" v-for="item in list" :key="item.id">
      <!-- header -->
      <div class="card-view-header lv-nav lv-nav-dual-band">
        <div class="lv-nav-left">
          <div class="lv-nav-info">
            <div class="lv-nav-title-wrapper">
              <div class="lv-nav-title">{{ item.title }}</div>
              <div style="margin-left: 10px" v-if="item.extension.username">
                <fts-profile
                  class="audit-profile"
                  :userName="item.extension.username"
                />
              </div>
            </div>
            <div class="lv-nav-info-wrapper">
              <!-- document -->
              <div class="lv-nav-document">
                <a
                  :href="locusUrl(item.id)"
                  class="card-view-document-id lv-nav-document-id"
                  @click.prevent="executeActivity({ id: 'quickView' }, [item])"
                  v-if="viewRight"
                >
                  {{ item.documentId }}
                </a>
                <div class="card-view-document-id lv-nav-document-id" v-else>
                  {{ item.documentId }}
                </div>
              </div>
              <!-- audit updated -->
              <div class="fts-audit">
                <fts-profile
                  class="audit-profile"
                  :userName="item.audit.updatedBy"
                />
                <fts-tippy
                  class="audit-date-wrapper"
                  :content="moment(item.audit.updatedDate).format('llll')"
                >
                  <div slot="trigger" class="audit-date">
                    {{ moment(item.audit.updatedDate).fromNow() }}
                  </div>
                </fts-tippy>
              </div>
              <!-- assign To -->
              <fts-assign-to
                :module-id="vectorId"
                :form-data="item"
                :view-mode="!assignRight"
                :comment-section="commentRight"
                class="card-view-assign"
                @refresh="refresh"
                v-if="assignColumn"
              />
            </div>
          </div>
        </div>
        <div class="lv-nav-right">
          <div class="lv-nav-actions">
            <!-- state -->
            <div class="lv-nav-state">
              <fts-state
                :state="item.state"
                :states="states"
                v-if="item.state && item.state.id"
              />
            </div>
            <!-- collaboration -->
            <div
              class="lv-nav-action lv-nav-collaboration"
              v-if="commentRight && item.lastComment && item.lastComment.id"
            >
              <fts-tippy
                :options="{ theme: 'light-border' }"
                :request="{
                  dispatchName: 'loadHistory',
                  payload: {
                    id: item.lastComment.id,
                    type: item.lastComment.type
                      ? item.lastComment.type
                      : 'comment',
                  },
                }"
                append-content-class="fts-collaboration-content"
              >
                <div
                  slot="trigger"
                  class="collaboration-trigger collaboration-comment"
                  :class="{
                    'collaboration-last': (item.collaborationFlags & 4) === 4,
                  }"
                >
                  <i class="fts-icon-comment"></i>
                </div>
                <template #content="{ content, loading }">
                  <template v-if="content">
                    <div class="collaboration-comment-audit">
                      <fts-profile :userName="content.createdBy" />
                      <div class="comment-from-now">
                        {{ moment(content.createdDate).fromNow() }}
                      </div>
                    </div>
                    <div
                      class="collaboration-comment-content"
                      v-html="translateCommentContent(content.detail)"
                    ></div>
                    <div class="collaboration-date">
                      {{ moment(content.createdDate).format("llll") }}
                    </div>
                  </template>
                  <fts-spinner :size="16" show v-else-if="loading" />
                </template>
              </fts-tippy>
              <span class="collaboration-count">{{ item.commentCount }}</span>
            </div>
            <!-- standard menu -->
            <fts-standard-menu       
              :item="item"
              :standardActions="standardActions"
              :moduleId="vectorId"
              :listId="listId"
              class="lv-nav-action"
              @executed="executeActivity($event, [item])"
            >
              <font-awesome-icon
                slot="toggle"
                :icon="['fas', 'ellipsis-v']"
                fixed-width
              />
            </fts-standard-menu>
          </div>
        </div>
      </div>
      <!-- body -->
      <div class="card-view-body">
        <div
          v-for="column in getColumns"
          :key="column.id"
          class="card-view-column"
          :class="{
            'full-width':
              column.type === 11 || column.type === 13 || column.type === 14,
          }"
        >
          <div class="form-element-label">
            <fts-moji
              :emoji="moduleEmoji(column.connectionId)"
              :size="14"
              class="form-element-emoji"
              v-if="column.connectionId && moduleEmoji(column.connectionId)"
            />
            <i
              class="form-element-icon"
              :class="informationIcon(column.type)"
              v-else
            ></i>
            <div class="form-element-name">{{ column.label || column.displayName }}</div>
          </div>
          <fts-list-view-display
            :form-data="item.extension"
            :item="column"
            :vector-id="vectorId"
            comfort-design
          />
        </div>
      </div>
      <!-- footer -->
      <div
        class="card-view-footer"
        v-if="
          getActivityList(item).length ||
          getActionList(item).length ||
          getDocumentList(item).length
        "
      >
        <div class="lv-actions">
          <!-- activities -->
          <fts-btn-hold
            outline
            :color="x.color || 'primary'"
            :emoji="x.emoji"
            text-transform-init
            class="btn-activity"
            @clickEvent="executeActivity(x, [item])"
            @longPressEvent="executeActivity(x, [item], true)"
            v-for="x in getActivityList(item)"
            :key="x.id"
          >
            <span class="btn-activity-label">
              {{ x.name }}
              <fts-test-badge :stage="x.development" v-if="x.development"></fts-test-badge>
            </span>
          </fts-btn-hold>
          <!-- actions -->
          <fts-btn
            outline
            :color="x.color || 'ternary-blue'"
            :emoji="x.emoji"
            text-transform-init
            class="btn-activity"
            @click="executeAction(x, [item])"
            v-for="x in getActionList(item)"
            :key="x.id"
          >
            <span :class="{ 'btn-activity-label': !x.color }">{{
              x.name
            }}</span>
          </fts-btn>
          <!-- document -->
          <fts-btn
            outline
            color="ternary-green"
            text-transform-init
            class="btn-activity"
            @click="executeDocument(x, [item])"
            v-for="x in getDocumentList()"
            :key="x.id"
          >
            <font-awesome-icon :icon="['far', 'file-pdf']" class="btn-activity-icon fts-file-pdf" v-if="x.typeId === 'pdf'" />
            <font-awesome-icon :icon="['far', 'file-alt']" class="btn-activity-icon fts-file-excel" v-else-if="x.typeId === 'spreadsheet'" />
            <font-awesome-icon :icon="['far', 'file-code']" class="btn-activity-icon fts-file-html" v-else-if="x.typeId === 'html'" />
            <font-awesome-icon :icon="['far', 'file']" class="btn-activity-icon fts-file-default" v-else />
            <span class="btn-activity-label">
              {{x.name}}
              <fts-test-badge :stage="x.development" v-if="x.development"></fts-test-badge>
            </span>
          </fts-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import commons from '../../commons/commons';
import { clientStore } from '../../store/clientStore';

import FTSAssignTo from '../FTSAssignTo';
import FTSBtn from '../FTSBtn';
import FTSBtnHold from '../FTSBtnHold';
import FTSListViewDisplay from '../FTSListViewDisplay';
import FTSMoji from '../FTSMoji';
import FTSProfile from '../FTSProfile';
import FTSSpinner from '../FTSSpinner';
import FTSStandardMenu from '../FTSStandardMenu.vue';
import FTSState from '../FTSState';
import FTSTippy from '../FTSTippy';
import FTSTestBadge from '../FTSTestBadge';

export default {
  props: {
    vectorId: [String, Number],
    listId: String,
    states: Array,
    users: Array,
    columns: {
      type: Array,
      default: () => [],
    },
    list: {
      type: Array,
      default: () => [],
    },
    standardActions: {
      type: Array,
      default: () => [],
    },
    actionList: {
      type: Array,
      default: () => [],
    },
    documentList: {
      type: Array,
      default: () => [],
    },
    activityList: {
      type: Array,
      default: () => [],
    },
  },
  components: {
    "fts-assign-to": FTSAssignTo,
    "fts-btn": FTSBtn,
    "fts-btn-hold": FTSBtnHold,
    "fts-list-view-display": FTSListViewDisplay,
    "fts-moji": FTSMoji,
    "fts-profile": FTSProfile,
    "fts-spinner": FTSSpinner,
    "fts-standard-menu": FTSStandardMenu,
    "fts-state": FTSState,
    "fts-tippy": FTSTippy,
    "fts-test-badge": FTSTestBadge
  },
  methods: {
    moment,
    informationIcon(typeId) {
      return commons.informationIcon(typeId);
    },
    moduleEmoji(connectionId) {
      const res = this.workspaceModules.find(x => x.id == connectionId);
      return res && res.emoji;
    },
    locusUrl(id) {
      return commons.clientPathUrl({
        moduleId: this.vectorId,
        listId: this.listId,
        id: id,
      });
    },
    getActivityList(item) {
      return this.activityList.filter((activity) => {
        return (
          !activity.fromStates ||
          activity.fromStates.some((x) => x === item.state.id)
        );
      });
    },
    getActionList(item) {
      /* custom visibility */
      return this.actionList.filter((action) => {
        const items = [item]; 
        if (action.isDefault) {
          return false;
        } else if (Boolean(action.condition)) {
          try {
            return Boolean(eval(action.condition));
          } catch(err) {
            console.error('[Custom:Condition error]', err);
            return false;
          }
        }
        return true;
      });
    },
    getDocumentList() {
      return this.documentList.filter((action) => {
        if (
          action.dataSourceId == "collection" ||
          action.dataSourceId == "logicService"
        )
          return false;
        return true;
      });
    },
    executeActivity(activity, items, fullView) {
      this.$emit("activityExecuted", {
        activity: activity,
        items: items,
        fullView: fullView,
      });
    },
    executeAction(action, items) {
      this.$emit("actionExecuted", {
        action: action,
        items: items,
      });
    },
    executeDocument(action, items) {
      this.$emit("documentExecuted", {
        action: action,
        items: items,
      });
    },
    refresh() {
      this.$emit("refresh");
    },
    translateCommentContent(content) {
      if (!!content)
        return commons.translateCommentContent(content, this.users);
    }
  },
  computed: {
    viewRight() {
      return (
        this.standardActions.indexOf("view") > -1 ||
        this.standardActions.indexOf("quickView") > -1
      );
    },
    assignRight() {
      return this.standardActions.indexOf("assign") > -1;
    },
    commentRight() {
      return this.standardActions.indexOf("comment") > -1;
    },
    getColumns() {
      return this.columns.filter((item) => (
        item.show !== false &&
        item.fieldName &&
        item.fieldName != "documentId" &&
        item.fieldName != "state.name" &&
        !item.system
      ));
    },
    workspaceModules() {
      return clientStore.getters.workspaceModules;
    },
    assignColumn() {
      return this.columns.find((x) => x.system && x.fieldName === "assignTo");
    },
  },
};
</script>

<style lang="scss" scoped>
@use "/scss/style.scss";
</style>

<style lang="scss">
@use "./src/assets/scss/vars.scss" as *;

.fts-card-view {
  color: var(--fts-text);
  .card-view-item {
    max-width: 1350px;
    margin: auto;
    box-shadow: var(--fts-elevation-3);
    border-radius: 0.25rem;
    background: var(--fts-card-background);
    &:not(:last-child) {
      margin-bottom: 1.5rem;
    }
    > div {
      &:first-child {
        border-top-left-radius: inherit;
        border-top-right-radius: inherit;
      }
      &:last-child {
        border-bottom-left-radius: inherit;
        border-bottom-right-radius: inherit;
      }
    }
    .card-view-header {
      padding: 0.75rem 0;
      &:hover {
        a.card-view-document-id {
          color: var(--fts-primary);
          cursor: pointer;
        }
      }
      .card-view-document-id {
        transition: color 0.3s ease-in-out;
        text-decoration: none;
        color: var(--fts-text);
      }
      .card-view-assign {
        margin-left: 0.75rem;
      }
    }
    .card-view-body {
      min-height: 100px;
      display: flex;
      flex-wrap: wrap;
      .card-view-column {
        padding: 1rem;
        flex: 1 1 33.333333%;
        &.full-width {
          max-width: 100%;
        }
        &:not(.full-width) {
          max-width: 33.333333%;
        }
        .form-element-label {
          display: flex;
          align-items: center;
          margin-bottom: 0.175rem;
          font-size: $font-size;
          line-height: $line-height;
          color: var(--fts-default);
          .form-element-emoji {
            margin-right: 0.575rem;
          }
          .form-element-icon {
            margin-right: 0.375rem;
            color: inherit;
            font-size: inherit;
          }
          .form-element-name {
            @include text-overflow-ellipsis;
            color: inherit;
            font-size: inherit;
          }
        }
        // Override
        .fts-display-table.comfort-design > tr > th {
          .col-header {
            font-size: 0.75rem;
            .col-header-icon {
              color: inherit;
              font-size: inherit;
            }
          }
        }
        // End Override
      }
    }
    .card-view-footer {
      padding: 1rem;
      border-top: 1px solid var(--fts-border);
    }
  }
}
</style>